import React from 'react';

import { CloseCircleFilled } from '@ant-design/icons';
import styled from '@emotion/styled';
import { Flex } from '@revenuemarket/cds';
import { RevenueMarketFontFamily, thematicColors, theme } from '@revenuemarket/design';
import { toast, ToastOptions, ToastContainer } from 'react-toastify';

import { InfoCircleIcon, SystemCheckCircle } from '../Icon';
import ExclamationCircle from '../Icon/components/ExclamationCircle';
import Typography from '../Typography';

/** 공통으로 적용되는 default option */
const defaultOptions: ToastOptions = {
  autoClose: 2000,
  hideProgressBar: true,
  closeOnClick: true,
  pauseOnHover: true,
  icon: false,
  position: toast.POSITION.TOP_RIGHT,
  progress: undefined,
  closeButton: false,
};

/** root에 적용할 wrapper */
export const StyledToastContainer = styled(ToastContainer)`
  width: auto;
  .Toastify__toast {
    padding: 8px 16px;
    min-height: 24px;
    border-radius: 6px;
    display: flex;
    align-items: center;
  }
  .Toastify__toast-body {
    font-family: ${RevenueMarketFontFamily};
    padding: 0;
    margin: 0;
  }
  @media only screen and (max-width: 480px) {
    &&&.Toastify__toast-container--top-center {
      left: 50%;
      transform: translateX(-50%);
      width: max-content;
    }
  }
`;

/** toast item component */
export const ToastItem = ({
  icon,
  message,
  link,
}: {
  icon: React.ReactNode;
  message: React.ReactNode;
  link?: React.ReactNode;
}) => {
  return (
    <Flex justify="space-between" align="center">
      <Flex gap={8} align="center">
        {icon}
        <Typography variant="body1" color={theme.colorTokens.textDefault}>
          {message}
        </Typography>
      </Flex>
      {link && (
        <Flex>
          <Typography variant="body1" color={thematicColors.secondary.regular}>
            {link}
          </Typography>
        </Flex>
      )}
    </Flex>
  );
};

/** 성공 관련 상황 toast */
export const success = (
  /** 표시할 텍스트 */
  message: React.ReactNode,
  /** toast option들 (react-toastify의 option) */
  options?: ToastOptions,
  /** link용 텍스트 */
  link?: React.ReactNode,
) => {
  return toast.success(
    <ToastItem icon={<SystemCheckCircle width={16} height={16} />} message={message} link={link} />,
    {
      ...defaultOptions,
      ...options,
    },
  );
};

/** 정보 표기용 toast */
export const info = (
  /** 표시할 텍스트 */
  message: React.ReactNode,
  /** toast option들 (react-toastify의 option) */
  options?: ToastOptions,
  /** link용 텍스트 */
  link?: React.ReactNode,
) => {
  return toast.info(
    <ToastItem
      icon={<InfoCircleIcon width={16} height={16} fill={thematicColors.secondary.regular} />}
      message={message}
      link={link}
    />,
    {
      ...defaultOptions,
      ...options,
    },
  );
};

/** 경고용 toast */
export const warning = (
  /** 표시할 텍스트 */
  message: React.ReactNode,
  /** toast option들 (react-toastify의 option) */
  options?: ToastOptions,
  /** link용 텍스트 */
  link?: React.ReactNode,
) => {
  return toast.warn(
    <ToastItem
      icon={<ExclamationCircle width={16} height={16} fill={theme.colorTokens.iconWarning} />}
      message={message}
      link={link}
    />,
    {
      ...defaultOptions,
      ...options,
    },
  );
};

/** 에러상황 toast */
export const error = (
  /** 표시할 텍스트 */
  message: React.ReactNode,
  /** toast option들 (react-toastify의 option) */
  options?: ToastOptions,
  /** link용 텍스트 */
  link?: React.ReactNode,
) => {
  return toast.error(
    <ToastItem
      icon={
        <CloseCircleFilled width={16} height={16} style={{ color: theme.colorTokens.iconDanger }} />
      }
      message={message}
      link={link}
    />,
    {
      ...defaultOptions,
      ...options,
    },
  );
};

export const toastLib = toast;

export default {
  success,
  info,
  warning,
  error,
  toastLib,
};
